<template>
  <span :class="['h-badge', `h-badge--${resolvedRole}`]" role="status">
    <span class="h-badge__dot" aria-hidden="true"></span>
    <span class="h-badge__word">{{ resolvedLabel }}</span>
  </span>
</template>

<script>
// Status pill — a coloured dot plus a word, on a tinted trio (bg/text/border).
// Colour NEVER carries meaning alone (DESIGN.md rule): the dot and the word do.
//
// Role is derived from `status` via the semantic map, or forced with `role`.
// Map follows design/components/badges-status.html's semantic table:
//   success  active, paid, ok, valid, within-limit
//   violet   trialing ONLY (a dedicated hue so it never collides with Sky info)
//   warning  past_due, expiring, approaching
//   danger   grace_period, revoked, failed, suspended, over-limit/overage
//   info     pending, issued, metered
//   neutral  cancelled, expired, offline, archived
export default {
  name: 'HermesStatusBadge',
  props: {
    // Machine status string, e.g. 'active', 'past_due', 'trialing'.
    status: { type: String, default: '' },
    // Force a role, bypassing the status→role map.
    role: { type: String, default: '' },
    // Override the displayed word (defaults to a humanised `status`).
    label: { type: String, default: '' },
  },
  data() {
    return {
      roleByStatus: {
        // success
        active: 'success', paid: 'success', ok: 'success', valid: 'success',
        succeeded: 'success', within_limit: 'success', delivered: 'success',
        // violet — trialing only
        trialing: 'violet', trial: 'violet',
        // warning
        past_due: 'warning', expiring: 'warning', approaching: 'warning',
        needs_test: 'warning', drift: 'warning',
        // danger
        grace_period: 'danger', revoked: 'danger', failed: 'danger',
        suspended: 'danger', overage: 'danger', over_limit: 'danger',
        blocked: 'danger', error: 'danger',
        // info
        pending: 'info', issued: 'info', metered: 'info', processing: 'info',
        // neutral
        cancelled: 'neutral', canceled: 'neutral', expired: 'neutral',
        offline: 'neutral', archived: 'neutral', inactive: 'neutral',
      },
    };
  },
  computed: {
    resolvedRole() {
      if (this.role) return this.role;
      const key = (this.status || '').toLowerCase();
      return this.roleByStatus[key] || 'neutral';
    },
    resolvedLabel() {
      if (this.label) return this.label;
      // Humanise: 'past_due' -> 'past due'; keep it lowercase per the mockups.
      return (this.status || '').replace(/_/g, ' ');
    },
  },
};
</script>

<style scoped>
.h-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 11px;
  border-radius: var(--h-radius-pill, 999px);
  font-family: var(--h-font-body, system-ui);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.2;
  border: 1px solid transparent;
  white-space: nowrap;
}
.h-badge__dot { width: 7px; height: 7px; border-radius: 50%; flex: none; }

.h-badge--success { background: var(--h-success-bg); color: var(--h-success-text); border-color: var(--h-success-border); }
.h-badge--success .h-badge__dot { background: var(--h-success); }
.h-badge--warning { background: var(--h-warning-bg); color: var(--h-warning-text); border-color: var(--h-warning-border); }
.h-badge--warning .h-badge__dot { background: var(--h-warning); }
.h-badge--danger  { background: var(--h-danger-bg);  color: var(--h-danger-text);  border-color: var(--h-danger-border); }
.h-badge--danger .h-badge__dot  { background: var(--h-danger); }
.h-badge--info    { background: var(--h-info-bg);    color: var(--h-info-text);    border-color: var(--h-info-border); }
.h-badge--info .h-badge__dot    { background: var(--h-info); }
.h-badge--neutral { background: var(--h-neutral-bg); color: var(--h-neutral-text); border-color: var(--h-neutral-border); }
.h-badge--neutral .h-badge__dot { background: var(--h-neutral); }
.h-badge--violet  { background: var(--h-violet-bg);  color: var(--h-violet-text);  border-color: var(--h-violet-border); }
.h-badge--violet .h-badge__dot  { background: var(--h-violet); }
</style>
